import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Play, Radio, Lock, Tv, Users, ChevronRight, Clock, Volume2, VolumeX } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, API } from '@/api';
import { resolveMediaUrl, getYouTubeEmbedUrl } from '@/utils/helpers';
import { SEO } from '@/components/common/SEO';
import { useViewerAuth } from '@/contexts/ViewerAuthContext';
import { TVSignIn } from '@/components/common/TVSignIn';

let MuxPlayer;
try { MuxPlayer = require('@mux/mux-player-react').default; } catch { MuxPlayer = null; }

const LiveTVPage = () => {
  const [nowPlaying, setNowPlaying] = useState(null);
  const [nextUp, setNextUp] = useState(null);
  const [transition, setTransition] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [viewerCount, setViewerCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [playbackToken, setPlaybackToken] = useState(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const navigate = useNavigate();
  const { viewer } = useViewerAuth();
  const [showTVSignIn, setShowTVSignIn] = useState(false);

  // Determine access for current content
  const hasAccess = useCallback((item) => {
    if (!item) return false;
    if (item.is_free) return true;
    if (!viewer) return false;
    // Check subscription
    const subStatus = viewer.subscription_status;
    const subExpires = viewer.subscription_expires;
    if (subStatus === 'active' && subExpires) {
      try {
        return new Date(subExpires) > new Date();
      } catch { return false; }
    }
    return false;
  }, [viewer]);

  // WebSocket connection to channel
  const connectWS = useCallback(() => {
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/channel`;
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        ws.send(JSON.stringify({ type: 'request_state' }));
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === 'now_playing') {
            setNowPlaying(msg.data);
            setTransition(null);
            setLoading(false);
          } else if (msg.type === 'transition') {
            setTransition(msg.data);
            setNextUp(msg.data.next);
            // Auto-clear transition after countdown
            setTimeout(() => setTransition(null), (msg.data.countdown || 10) * 1000);
          } else if (msg.type === 'viewer_count') {
            setViewerCount(msg.data.count);
          }
        } catch (e) {
          console.error('WS parse error:', e);
        }
      };

      ws.onclose = () => {
        setConnected(false);
        // Reconnect after 3 seconds
        reconnectRef.current = setTimeout(connectWS, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      console.error('WS connection error:', e);
      reconnectRef.current = setTimeout(connectWS, 5000);
    }
  }, []);

  // Start WS on mount
  useEffect(() => {
    connectWS();

    // Also load schedule via REST
    const loadSchedule = async () => {
      try {
        const res = await api.getChannelSchedule();
        setSchedule(res.data.schedule || []);
      } catch { /* silent */ }
    };
    loadSchedule();

    // Also get current state via REST (fallback)
    const loadNow = async () => {
      try {
        const res = await api.getChannelNow();
        if (res.data.playing) {
          setNowPlaying(res.data);
          setLoading(false);
        } else {
          setLoading(false);
        }
      } catch { setLoading(false); }
    };
    setTimeout(loadNow, 2000); // Give WS a chance first

    // Keep WS alive with pings
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connectWS]);

  // Fetch Mux playback token when now_playing changes to a paid Mux game
  useEffect(() => {
    if (!nowPlaying?.game_id || !hasAccess(nowPlaying)) {
      setPlaybackToken(null);
      return;
    }
    const fetchToken = async () => {
      try {
        const res = await api.getMuxPlaybackToken(nowPlaying.game_id);
        setPlaybackToken(res.data.token);
      } catch { setPlaybackToken(null); }
    };
    fetchToken();
  }, [nowPlaying, hasAccess]);

  const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // ============== RENDER PLAYER ==============
  const renderPlayer = () => {
    if (!nowPlaying) return null;
    const canWatch = hasAccess(nowPlaying);

    // PAYWALL OVERLAY — redirect to NLS+ (temporary)
    if (!canWatch && !nowPlaying.is_free) {
      window.location.href = 'https://plus.newlogicalsports.com';
      return (
        <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden flex items-center justify-center" data-testid="channel-paywall">
          <p className="text-gray-400">Redirecting to NLS+...</p>
        </div>
      );
    }

    // GAME with video sources
    if (nowPlaying.content_type === 'game' && nowPlaying.video_sources?.length > 0) {
      const source = nowPlaying.video_sources[0];

      // YouTube embed
      if (source.type === 'youtube') {
        const embedUrl = getYouTubeEmbedUrl(source.url);
        if (embedUrl) {
          return (
            <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden" data-testid="channel-player">
              <iframe
                src={`${embedUrl}?autoplay=1&mute=${muted ? 1 : 0}`}
                className="w-full h-full"
                allowFullScreen
                allow="autoplay; encrypted-media"
                title={nowPlaying.title}
              />
              {renderOverlay()}
            </div>
          );
        }
      }

      // Mux playback
      if ((source.type === 'mux' || source.type === 'uscreen') && MuxPlayer) {
        const playbackId = source.mux_playback_id || source.playback_id;
        if (playbackId) {
          return (
            <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden" data-testid="channel-player">
              <MuxPlayer
                playbackId={playbackId}
                tokens={playbackToken ? { playback: playbackToken } : undefined}
                autoPlay="muted"
                muted={muted}
                style={{ width: '100%', height: '100%' }}
              />
              {renderOverlay()}
            </div>
          );
        }
      }
    }

    // Direct video URL (promos, commercials)
    if (nowPlaying.video_url) {
      const embedUrl = getYouTubeEmbedUrl(nowPlaying.video_url);
      if (embedUrl) {
        return (
          <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden" data-testid="channel-player">
            <iframe
              src={`${embedUrl}?autoplay=1&mute=${muted ? 1 : 0}&loop=1`}
              className="w-full h-full"
              allowFullScreen
              allow="autoplay; encrypted-media"
              title={nowPlaying.title}
            />
            {renderOverlay()}
          </div>
        );
      }

      return (
        <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden" data-testid="channel-player">
          <video
            src={nowPlaying.video_url}
            autoPlay
            muted={muted}
            className="w-full h-full object-contain"
            onEnded={() => wsRef.current?.send(JSON.stringify({ type: 'request_state' }))}
          />
          {renderOverlay()}
        </div>
      );
    }

    // Standby / no video
    return (
      <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden flex items-center justify-center" data-testid="channel-standby">
        <div className="text-center">
          <Tv className="w-16 h-16 text-gray-700 mx-auto mb-4" />
          <h3 className="text-xl text-gray-400 font-semibold">
            {nowPlaying.content_type === 'standby' ? "We'll Be Right Back" : nowPlaying.title || 'Channel Loading...'}
          </h3>
          <p className="text-gray-600 text-sm mt-2">Stay tuned — the next stream starts soon</p>
        </div>
        {renderOverlay()}
      </div>
    );
  };

  // ============== NOW PLAYING OVERLAY ==============
  const renderOverlay = () => (
    <>
      {/* Top bar — Live badge + viewer count */}
      <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/60 to-transparent flex items-center justify-between pointer-events-none">
        <div className="flex items-center gap-2">
          <Badge className="bg-red-500 text-white flex items-center gap-1.5 px-2.5 py-1">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            NLS LIVE
          </Badge>
          {nowPlaying?.league_name && (
            <span className="text-white/80 text-xs font-medium">{nowPlaying.league_name}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-white/70 text-xs flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {viewerCount}
          </span>
        </div>
      </div>

      {/* Bottom bar — Now playing + mute */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent pointer-events-auto">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-semibold text-sm">{nowPlaying?.title}</p>
            {nowPlaying?.sport_name && (
              <p className="text-white/60 text-xs">{nowPlaying.sport_name}</p>
            )}
          </div>
          <button
            onClick={() => setMuted(!muted)}
            className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
            data-testid="mute-toggle"
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Transition overlay — "Up Next" */}
      {transition && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-10 animate-in fade-in" data-testid="transition-overlay">
          <div className="text-center">
            <p className="text-sky-400 text-xs font-bold uppercase tracking-widest mb-2">Up Next</p>
            <h3 className="text-white text-2xl font-bold mb-1">{transition.next?.title}</h3>
            {transition.next?.league_name && (
              <p className="text-gray-400 text-sm">{transition.next.league_name}</p>
            )}
            <p className="text-white/50 text-sm mt-4">Starting in {transition.countdown}s</p>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-950" data-testid="live-channel-page">
      <SEO title="NLS Live Channel" description="Watch live sports on New Logical Sports — always on, always live" />

      <div className="max-w-6xl mx-auto px-4 pt-20 pb-12">
        {/* Channel Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Radio className="w-6 h-6 text-red-500" />
            <h1 className="text-2xl font-bold text-white">NLS Live</h1>
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-xs text-gray-500">{connected ? 'Connected' : 'Reconnecting...'}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Users className="w-4 h-4" />
            <span>{viewerCount} watching</span>
          </div>
        </div>

        {/* Player */}
        {loading ? (
          <div className="w-full aspect-video bg-gray-900 rounded-lg flex items-center justify-center animate-pulse">
            <div className="text-center">
              <Tv className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500">Tuning in...</p>
            </div>
          </div>
        ) : !nowPlaying ? (
          <div className="w-full aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <Tv className="w-16 h-16 text-gray-700 mx-auto mb-4" />
              <h3 className="text-xl text-gray-400 font-semibold">No Broadcast Right Now</h3>
              <p className="text-gray-600 text-sm mt-2">Check the schedule below for upcoming content</p>
            </div>
          </div>
        ) : (
          renderPlayer()
        )}

        {/* TV Sign-In Overlay */}
        {showTVSignIn && !viewer && (
          <div className="mt-4 bg-gray-900 border border-sky-500/30 rounded-xl overflow-hidden" data-testid="tv-signin-overlay">
            <div className="flex items-center justify-between px-4 pt-3">
              <span className="text-xs text-sky-400 font-semibold uppercase tracking-widest">TV Sign In</span>
              <button onClick={() => setShowTVSignIn(false)} className="text-gray-500 hover:text-white text-xs">Close</button>
            </div>
            <TVSignIn onSignedIn={() => {
              setShowTVSignIn(false);
              window.location.reload();
            }} />
          </div>
        )}

        {/* Now Playing Info Bar */}
        {nowPlaying && !showTVSignIn && (
          <div className="mt-4 bg-gray-900 border border-gray-800 rounded-lg p-4 flex items-center justify-between" data-testid="now-playing-info">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-red-500/20 text-red-400 border border-red-500/30 text-xs">NOW</Badge>
                <span className="text-white font-semibold">{nowPlaying.title}</span>
              </div>
              <p className="text-gray-500 text-xs">
                {nowPlaying.league_name && `${nowPlaying.league_name} · `}
                {nowPlaying.content_type === 'game' ? 'Live Game' : nowPlaying.content_type}
                {nowPlaying.is_free ? ' · Free' : ' · Subscriber Only'}
              </p>
            </div>
            {nowPlaying.game_id && hasAccess(nowPlaying) && (
              <Link to={`/watch/${nowPlaying.game_id}`}>
                <Button size="sm" variant="outline" className="border-sky-500/30 text-sky-400 hover:bg-sky-500/10">
                  <Play className="w-3.5 h-3.5 mr-1" /> Full Watch Page
                </Button>
              </Link>
            )}
          </div>
        )}

        {/* Today's Schedule */}
        <div className="mt-8">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-sky-400" />
            Today's Schedule
          </h2>

          {schedule.length === 0 ? (
            <div className="text-center py-8 bg-gray-900/50 rounded-lg border border-gray-800">
              <p className="text-gray-500">No schedule set for today.</p>
              <p className="text-gray-600 text-xs mt-1">The admin will add programming soon.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {schedule.map(item => {
                const isNow = nowPlaying?.id === item.id;
                const isPast = new Date(item.end_time) < new Date();
                const isFree = item.is_free;
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-colors ${
                      isNow
                        ? 'bg-sky-500/10 border border-sky-500/30'
                        : isPast
                          ? 'opacity-50'
                          : 'bg-gray-900/50 border border-gray-800 hover:border-gray-700'
                    }`}
                    data-testid={`schedule-item-${item.id}`}
                  >
                    <div className="w-20 text-right shrink-0">
                      <span className="text-gray-400 text-xs font-mono">{formatTime(item.start_time)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {isNow && (
                          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse shrink-0" />
                        )}
                        <span className={`text-sm font-medium truncate ${isNow ? 'text-white' : 'text-gray-300'}`}>
                          {item.title || 'Untitled'}
                        </span>
                      </div>
                      {item.league_name && (
                        <p className="text-gray-600 text-xs mt-0.5">{item.league_name}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                        item.content_type === 'game'
                          ? 'bg-sky-500/10 border-sky-500/20 text-sky-400'
                          : item.content_type === 'commercial' || item.content_type === 'promo'
                            ? 'bg-purple-500/10 border-purple-500/20 text-purple-400'
                            : 'bg-gray-500/10 border-gray-500/20 text-gray-400'
                      }`}>
                        {item.content_type}
                      </span>
                      {!isFree && (
                        <Lock className="w-3.5 h-3.5 text-amber-500" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveTVPage;
